import { useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import * as Sentry from '@sentry/react';
import { useBreakPoints, useFeatureFlag_Core } from 'core';
import { AnnouncementCenter } from 'core/Packages';
import { OneToTwoLayoutTemplate, Stack, Text, Title } from 'ui';

import { MyCausesImpactCard } from '../give/MyCausesImpactCard/MyCausesImpactCard';
import { MyPersonalizedStartCard } from '../give/MyPersonalizedStartCard/MyPersonalizedStartCard';
import { HowToGiveback } from './HowToGiveback/components/HowToGivebackCards';
import { MyFavoriteCauses } from './MyFavoriteCauses/MyFavoriteCauses';
import { MyFundraisingPagesSection } from './MyFundraisingPagesSection/MyFundraisingPagesSection';
import { Shortcuts } from './Shortcuts/Shortcuts';

const HomeContent = () => {
  const { isSmallScreen, isMediumScreen } = useBreakPoints();
  const { isAuthenticated } = useAuthentication();

  const { isFeatureEnabled } = useFeatureFlag_Core({
    featureId: 'user.account_page',
  });

  const showShortcuts = useMemo(() => {
    return isAuthenticated && isMediumScreen && !isFeatureEnabled;
  }, [isAuthenticated, isMediumScreen, isFeatureEnabled]);

  return (
    <Stack gap={isSmallScreen ? 'lg' : 'xl'}>
      {isAuthenticated && <MyFavoriteCauses />}
      {isAuthenticated && <MyFundraisingPagesSection />}
      {showShortcuts && <Shortcuts />}
      {isAuthenticated && (
        // Ensure any pendo errors are not thrown to the user and instead of showing a fall back, just log them to sentry
        <Sentry.ErrorBoundary>
          <AnnouncementCenter />
        </Sentry.ErrorBoundary>
      )}
      {!isAuthenticated && (
        <Stack gap={isSmallScreen ? 'md' : 'lg'}>
          <Title order={2} fz={24}>
            What is Givebacks?
          </Title>
          <Text type="body">
            Givebacks is an inclusive giving ecosystem that helps people effortlessly move more
            money to the causes they care about. No matter how big or small, everyone can and will
            make a difference when they’re a part of Givebacks.
          </Text>
        </Stack>
      )}
      <HowToGiveback />
    </Stack>
  );
};

const Home = () => {
  const { isAuthenticated } = useAuthentication();
  const { isMediumScreen } = useBreakPoints();

  const { isFeatureEnabled } = useFeatureFlag_Core({
    featureId: 'user.account_page',
  });

  const showShortcuts = useMemo(() => {
    return isAuthenticated && !isMediumScreen && !isFeatureEnabled;
  }, [isAuthenticated, isMediumScreen, isFeatureEnabled]);

  return (
    <OneToTwoLayoutTemplate
      showLeftSection={isAuthenticated}
      leftSection={
        <Stack gap="lg">
          <MyPersonalizedStartCard />
          <MyCausesImpactCard />
          {showShortcuts && <Shortcuts />}
        </Stack>
      }
      rightSection={<HomeContent />}
    />
  );
};

export { Home };
